import React, { useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import "./styles/app.sass";
import "./utils/authHelpers"; // Load auth helpers for debugging
import Page from "./components/Page";
import Landing from "./screens/Landing";
import WhatIsPostaway from "./screens/InfoPage/WhatIsPostaway";
import WhyPostaway from "./screens/InfoPage/WhyPostaway";
import HowToUsePostaway from "./screens/InfoPage/HowToUsePostaway";
import OurStory from "./screens/InfoPage/OurStory";
import Contact from "./screens/InfoPage/Contact";
import CreatorSignup from "./screens/InfoPage/CreatorSignup";
import HostSignup from "./screens/InfoPage/HostSignup";
import HostTypeSelect from "./screens/InfoPage/HostTypeSelect";
import ProfileSelector from "./screens/InfoPage/ProfileSelector";
import CreatorLogin from "./screens/InfoPage/CreatorLogin";
import HostLogin from "./screens/InfoPage/HostLogin";
import Login from "./screens/InfoPage/Login";
import TravelerSignup from "./screens/InfoPage/TravelerSignup";
import TravelerLogin from "./screens/InfoPage/TravelerLogin";
import PartnerLogin from "./screens/InfoPage/PartnerLogin";
import PartnerSignup from "./screens/InfoPage/PartnerSignup";
import SummitApplication from "./screens/InfoPage/SummitApplication";
import Summit2027 from "./screens/InfoPage/Summit2027";
import InnerCircle from "./screens/InnerCircle";
import InnerCircleConfirmation from "./screens/InnerCircle/Confirmation";
import MultiPersonaSignup from "./screens/InfoPage/MultiPersonaSignup";
import PartnerCheckout from "./screens/InfoPage/PartnerCheckout";
import PartnerWelcome from "./screens/InfoPage/PartnerWelcome";
import ForgotPassword from "./screens/InfoPage/ForgotPassword";
import ResetPassword from "./screens/InfoPage/ResetPassword";
import TravelerOnboarding from "./screens/TravelerOnboarding";
import CreatorOnboarding from "./screens/CreatorOnboarding";
import HostOnboarding from "./screens/HostOnboarding";
import HostCheckout from "./screens/HostCheckout";
import CollabBoard from "./screens/CollabBoard";
import TravelBoard from "./screens/TravelBoard";
import Itineraries from "./screens/Itineraries";
import Success from "./screens/Success";
import Profile from "./screens/Profile";
import Creators from "./screens/Creators";
import ComingSoonPlaceholder from "./screens/Creators/ComingSoonPlaceholder";
import Hosts from "./screens/Hosts";
import HostResources from "./screens/Hosts/HostResources";
import LearnHome from "./screens/Learn/LearnHome";
import LearnLibrary from "./screens/Learn/LearnLibrary";
import LearnCourse from "./screens/Learn/LearnCourse";
import LearnWatch from "./screens/Learn/LearnWatch";
import Partners from "./screens/Partners";
import PartnerSettings from "./screens/PartnerSettings";
import PlanSettings from "./screens/PlanSettings";
import CombineAccounts from "./screens/CombineAccounts";
import PropertyManagement from "./screens/PropertyManagement";
import PropertyEditor from "./screens/PropertyEditor";
import HostDashboard from "./screens/HostDashboard";
import CreatorDashboard from "./screens/CreatorDashboard";
import TravelerSettings from "./screens/TravelerSettings";
import Privacy from "./screens/Privacy";
import MemberAgreement from "./screens/InfoPage/MemberAgreement";
import PrivacyPolicy from "./screens/InfoPage/PrivacyPolicy";
import Messages from "./screens/Messages";
import CollabWorkspaceScreen from "./screens/CollabWorkspaceScreen";
import Collabs from "./screens/Collabs";
import DraftEditor from "./screens/DraftEditor";
import GetFeatured from "./screens/GetFeatured";
import SpotlightQuestionnaire from "./screens/SpotlightQuestionnaire";
import Spotlight from "./screens/Spotlight";
import GetVerified from "./screens/GetVerified";
import Support from "./screens/Support";
import ReviewsFeed from "./screens/ReviewsFeed";
import Storefronts from "./screens/Storefronts";
import PublicCollab from "./screens/PublicCollab";
import PublicProperty from "./screens/PublicProperty";
import Admin from "./screens/Admin";
import PartnerApplications from "./screens/Admin/PartnerApplications";
import UserMerge from "./screens/Admin/UserMerge";
import CollabReconciliation from "./screens/Admin/CollabReconciliation";
import DashboardRedirect from "./screens/DashboardRedirect";
import { ToastManager } from "./components/Messaging/Toast";
import { ChatProvider } from "./contexts/ChatContext";
import { PersonaProvider } from "./contexts/PersonaContext";
import WelcomeModal from "./components/WelcomeModal";
import SummitPromoModal from "./components/SummitPromoModal";
import AuthSessionManager from "./components/AuthSessionManager";
import { getReturnTo } from "./utils/returnTo";

function RewardfulConvertOnSuccess() {
    const location = useLocation();
    useEffect(() => {
        try {
            const params = new URLSearchParams(location.search);
            if (params.get('checkout') === 'success') {
                const email = localStorage.getItem('postaway_email');
                if (window.rewardful && email) {
                    window.rewardful('convert', { email });
                }
            }
        } catch (_) {}
    }, [location.search]);
    return null;
}

function WelcomeModalHandler() {
    const location = useLocation();
    const [showModal, setShowModal] = React.useState(false);
    const [firstName, setFirstName] = React.useState(null);
    const [userEmail, setUserEmail] = React.useState(null);
    const [userType, setUserType] = React.useState(null);
    const [hasShown, setHasShown] = React.useState(false);
    const [pendingReturnTo, setPendingReturnTo] = React.useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const isCheckoutSuccess = params.get('checkout') === 'success';
        
        // Check if we've already shown the modal for this session
        if (isCheckoutSuccess && !hasShown) {
            const returnTo = getReturnTo();
            setPendingReturnTo(returnTo || null);
            // Get email + user type from localStorage
            let email = '';
            let storedUserType = '';
            try {
                email = localStorage.getItem('postaway_email') || '';
                storedUserType = localStorage.getItem('postaway_user_type') || '';
            } catch (_) {}

            setUserType(storedUserType || null);

            if (email) {
                setUserEmail(email);
                // Fetch user's first name
                fetch(`/api/user/info?email=${encodeURIComponent(email)}`)
                    .then(res => res.json())
                    .then(data => {
                        if (data.ok && data.first_name) {
                            setFirstName(data.first_name);
                        }
                        setShowModal(true);
                        setHasShown(true);
                    })
                    .catch(err => {
                        console.error('Error fetching user info:', err);
                        // Show modal anyway without first name
                        setShowModal(true);
                        setHasShown(true);
                    });
            } else {
                // Show modal without first name if no email
                setShowModal(true);
                setHasShown(true);
            }
        } else if (!isCheckoutSuccess) {
            // Reset when checkout=success is not in URL
            setHasShown(false);
            setShowModal(false);
            setPendingReturnTo(null);
        }
    }, [location.search, hasShown]);

    const handleClose = () => {
        setShowModal(false);
        if (pendingReturnTo) {
            window.location.assign(pendingReturnTo);
            return;
        }
        // Remove checkout=success from URL without reloading
        const params = new URLSearchParams(location.search);
        params.delete('checkout');
        const newUrl = params.toString() 
            ? `${window.location.pathname}?${params.toString()}`
            : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
    };

    return <WelcomeModal isOpen={showModal} onClose={handleClose} firstName={firstName} email={userEmail} userType={userType} />;
}

function App() {
    return (
        <ChatProvider>
        <PersonaProvider>
            <AuthSessionManager />
            <ToastManager />
            <RewardfulConvertOnSuccess />
            <WelcomeModalHandler />
            <SummitPromoModal />
            <Analytics />
            <Routes>
            <Route path="c/p/:idOrSlug" element={<PublicCollab type="property" />} />
            <Route path="c/u/:idOrHandle" element={<PublicCollab type="creator" />} />
            <Route path="p/:idOrSlug" element={<PublicProperty />} />
            <Route path="/">
                <Route index element={<Page teaser><Landing /></Page>} />
                {/* Convenience redirect so localhost:3000/home doesn't show a blank page */}
                <Route path="home" element={<Navigate to="/" replace />} />
                <Route path="what-is-postaway" element={<Page teaser><WhatIsPostaway /></Page>} />
                <Route path="why-postaway" element={<Page teaser><WhyPostaway /></Page>} />
                <Route path="how-to-use-postaway" element={<Page teaser><HowToUsePostaway /></Page>} />
                <Route path="our-story" element={<Page teaser><OurStory /></Page>} />
                <Route path="contact" element={<Page teaser><Contact /></Page>} />
                <Route path="profile-selector" element={<Page teaser><ProfileSelector /></Page>} />
                <Route path="creator-signup" element={<Page teaser><CreatorSignup /></Page>} />
                <Route path="host-select" element={<Page teaser><HostTypeSelect /></Page>} />
                <Route path="host-signup" element={<Page teaser><HostSignup /></Page>} />
                <Route path="login" element={<Page teaser><Login /></Page>} />
                <Route path="dashboard" element={<Page teaser><DashboardRedirect /></Page>} />
                <Route path="creator-login" element={<Page teaser><CreatorLogin /></Page>} />
                <Route path="host-login" element={<Page teaser><HostLogin /></Page>} />
                <Route path="traveler-signup" element={<Page teaser><TravelerSignup /></Page>} />
                <Route path="traveler-login" element={<Page teaser><TravelerLogin /></Page>} />
                <Route path="partner-login" element={<Page teaser><PartnerLogin /></Page>} />
                <Route path="partner-signup" element={<Page teaser><PartnerSignup /></Page>} />
                <Route path="hospitality-summit" element={<Page teaser><SummitApplication /></Page>} />
                <Route path="hospitality-summit-2027" element={<Page teaser><Summit2027 /></Page>} />
                <Route path="inner-circle" element={<Page teaser><InnerCircle /></Page>} />
                <Route path="inner-circle/confirmation" element={<Page teaser><InnerCircleConfirmation /></Page>} />
                <Route path="multi-signup" element={<Page teaser><MultiPersonaSignup /></Page>} />
                <Route path="partner-checkout" element={<Page teaser><PartnerCheckout /></Page>} />
                <Route path="partner-welcome" element={<Page teaser><PartnerWelcome /></Page>} />
                <Route path="partner-settings" element={<Page teaser><PartnerSettings /></Page>} />
                <Route path="plan-settings" element={<Page teaser><PlanSettings /></Page>} />
                <Route path="combine-accounts" element={<Page teaser><CombineAccounts /></Page>} />
                <Route path="forgot-password" element={<Page teaser><ForgotPassword /></Page>} />
                <Route path="reset-password" element={<Page teaser><ResetPassword /></Page>} />
                <Route path="traveler/onboarding" element={<Page teaser><TravelerOnboarding /></Page>} />
                <Route path="creator/onboarding" element={<Page teaser><CreatorOnboarding /></Page>} />
                <Route path="host/onboarding" element={<Page teaser><HostOnboarding /></Page>} />
                <Route path="host/checkout" element={<Page teaser><HostCheckout /></Page>} />
                <Route path="host-dashboard" element={<Page teaser><HostDashboard /></Page>} />
                <Route path="creator-dashboard" element={<Page teaser><CreatorDashboard /></Page>} />
                <Route path="traveler-settings" element={<Page teaser><TravelerSettings /></Page>} />
                <Route path="property-management" element={<Page teaser><PropertyManagement /></Page>} />
                <Route path="property/editor" element={<Page teaser><PropertyEditor /></Page>} />
                <Route path="collab-board" element={<Page teaser><CollabBoard /></Page>} />
                <Route path="travel-board" element={<Page teaser><TravelBoard /></Page>} />
                <Route path="creators" element={<Page teaser><Creators /></Page>} />
                <Route path="creators/storefronts" element={<Page teaser><Storefronts type="creator" /></Page>} />
                <Route path="creators/reviews" element={<Page teaser><ReviewsFeed type="creator" /></Page>} />
                <Route path="creators/get-featured" element={<Page teaser><SpotlightQuestionnaire type="creator" /></Page>} />
                <Route path="creators/spotlight" element={<Page teaser><Spotlight type="creator" /></Page>} />
                <Route path="creators/resources" element={<Page teaser><ComingSoonPlaceholder title="Creator Resources" /></Page>} />
                <Route path="creators/refer" element={<Page teaser><ComingSoonPlaceholder title="Refer a Creator" /></Page>} />
                <Route path="hosts" element={<Page teaser><Hosts /></Page>} />
                <Route path="partners" element={<Page teaser><Partners /></Page>} />
                <Route path="hosts/storefronts" element={<Page teaser><Storefronts type="host" /></Page>} />
                <Route path="hosts/reviews" element={<Page teaser><ReviewsFeed type="host" /></Page>} />
                <Route path="hosts/get-featured" element={<Page teaser><SpotlightQuestionnaire type="host" /></Page>} />
                <Route path="hosts/spotlight" element={<Page teaser><Spotlight type="host" /></Page>} />
                <Route path="hosts/resources" element={<Page teaser><HostResources /></Page>} />
                <Route path="learn" element={<Page teaser><LearnHome /></Page>} />
                <Route path="learn/library" element={<Page teaser><LearnLibrary /></Page>} />
                <Route path="learn/:slug" element={<Page teaser><LearnCourse /></Page>} />
                <Route path="learn/:slug/watch/:position" element={<Page teaser><LearnWatch /></Page>} />
                <Route path="hosts/refer" element={<Page teaser><ComingSoonPlaceholder title="Refer a Host" /></Page>} />
                <Route path="collab-board/best-practices" element={<Page teaser><ComingSoonPlaceholder title="Best Practices" /></Page>} />
                <Route path="collab-board/faq" element={<Page teaser><ComingSoonPlaceholder title="Collab FAQ" /></Page>} />
                <Route path="itineraries" element={<Page teaser><Itineraries /></Page>} />
                <Route path="messages" element={<Page teaser><Messages /></Page>} />
                <Route path="collabs" element={<Page teaser><Collabs /></Page>} />
                <Route path="collabs/workspace" element={<Page teaser><CollabWorkspaceScreen /></Page>} />
                <Route path="collabs/draft" element={<Page teaser><DraftEditor /></Page>} />
                <Route path="get-featured" element={<Page teaser><GetFeatured /></Page>} />
                <Route path="get-verified" element={<Page teaser><GetVerified /></Page>} />
                <Route path="support" element={<Page teaser><Support /></Page>} />
                <Route path="support/:slug" element={<Page teaser><Support /></Page>} />
                <Route path="profile" element={<Page teaser><Profile /></Page>} />
                <Route path="success" element={<Page teaser><Success /></Page>} />
                <Route path="privacy" element={<Page teaser><Privacy /></Page>} />
                <Route path="member-agreement" element={<Page teaser><MemberAgreement /></Page>} />
                <Route path="privacy-policy" element={<Page teaser><PrivacyPolicy /></Page>} />
                <Route path="admin" element={<Page teaser><Admin /></Page>} />
                <Route path="admin/partner-applications" element={<Page teaser><PartnerApplications /></Page>} />
                <Route path="admin/users/merge" element={<Page teaser><UserMerge /></Page>} />
                <Route path="admin/collab-reconciliation" element={<Page teaser><CollabReconciliation /></Page>} />
            </Route>
            </Routes>
        </PersonaProvider>
        </ChatProvider>
    );
}

export default App;
